import React, { useState, useEffect } from "react";
import {
  LayoutDashboard,
  Users,
  HardDrive,
  FileText,
  CalendarDays,
  UserCog,
  Brain,
  X,
  Menu,
  ChevronLeft,
  Pin,
  PinOff,
  Zap,
  Palette,
  Settings,
  MessageSquare,
  Timer,
  CalendarCheck,
  Package,
  Car,
  Receipt
} from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import ThemeCustomizer from "../theme/ThemeCustomizer";
import { usePermisos } from "../../hooks/usePermisos";
import defaultLogo from "../../assets/logo-sidebar.png";

// Mapa de módulos con sus configuraciones
const modulosDisponibles = {
  dashboard: { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  control_horas: { key: "control_horas", label: "Control de Horas", icon: Timer, dividerBefore: "Gestión" },
  calendario_visitas: { key: "calendario_visitas", label: "Calendario", icon: CalendarCheck },
  clientes: { key: "clientes", label: "Clientes", icon: Users },
  equipos: { key: "equipos", label: "Equipos", icon: HardDrive },
  ordenes: { key: "ordenes", label: "Órdenes", icon: FileText },
  presupuestos: { key: "presupuestos", label: "Presupuestos", icon: FileText },
  agenda: { key: "agenda", label: "Agendamiento", icon: CalendarDays, dividerBefore: "Planificación" },
  agendamientos_inteligentes: { key: "agendamientos_inteligentes", label: "IA Schedules", icon: Brain },
  usuarios: { key: "usuarios", label: "Usuarios", icon: UserCog, dividerBefore: "Administración" },
  admin_avanzado: { key: "admin_avanzado", label: "Panel Avanzado", icon: Settings },
  pendientes: { key: "pendientes", label: "Pendientes", icon: FileText },
  tickets: { key: "tickets", label: "Tickets", icon: MessageSquare },
  // Módulos operativos nuevos
  recepciones_equipos: { key: "recepciones_equipos", label: "Recep. Equipos", icon: Package, dividerBefore: "Operaciones" },
  entregas_documentos: { key: "entregas_documentos", label: "Entrega Docs.", icon: Receipt },
  movilidad: { key: "movilidad", label: "Movilidad", icon: Car },
  // Módulos de cliente
  dashboard_cliente: { key: "dashboard_cliente", label: "Inicio", icon: LayoutDashboard },
  mis_ordenes: { key: "mis_ordenes", label: "Mis Órdenes", icon: FileText, dividerBefore: "Mis Servicios" },
  mis_equipos: { key: "mis_equipos", label: "Mis Equipos", icon: HardDrive },
  mis_agendamientos: { key: "mis_agendamientos", label: "Mi Calendario", icon: CalendarDays },
  mis_horas: { key: "mis_horas", label: "Mis Horas", icon: Timer },
  monitor_servicios: { key: "monitor_servicios", label: "Estado del Sistema", icon: Zap },
};

// Items por rol (SOLO para clientes que tienen módulos especiales)
const itemsCliente = [
  "dashboard_cliente",
  "monitor_servicios",
  "tickets",
  "mis_ordenes",
  "mis_equipos",
  "mis_agendamientos",
  "mis_horas"
];

export default function SmartSidebar({ rol, seccion, onChange, mobileOpen, setMobileOpen }) {
  // Hook del tema
  const { settings, currentTheme, getThemeClasses } = useTheme();
  
  // Hook de permisos
  const { modulos, loading, puedeVer, esAdmin } = usePermisos();
  
  // Hook de autenticación para obtener el username
  const { user } = useAuth();
  
  // Estado del customizador
  const [showCustomizer, setShowCustomizer] = useState(false);
  
  // Cargar preferencias del localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved ? JSON.parse(saved) : false;
  });
  
  const [isPinned, setIsPinned] = useState(() => {
    const saved = localStorage.getItem('sidebar-pinned');
    return saved ? JSON.parse(saved) : true;
  });
  
  const [isHovered, setIsHovered] = useState(false);

  // Construir items dinámicamente basado en permisos
  let items = [];
  
  // El usuario "admin" (username="admin") SIEMPRE ve todos los módulos
  const esSuperAdmin = user?.username === "admin";
  
  if (rol === "admin") {
    if (esSuperAdmin) {
      // El usuario "admin" ve TODOS los módulos siempre
      items = [
        modulosDisponibles.dashboard,
        modulosDisponibles.pendientes,
        modulosDisponibles.tickets,
        modulosDisponibles.control_horas,
        modulosDisponibles.calendario_visitas,
        modulosDisponibles.clientes,
        modulosDisponibles.equipos,
        modulosDisponibles.ordenes,
        modulosDisponibles.presupuestos,
        modulosDisponibles.agenda,
        modulosDisponibles.agendamientos_inteligentes,
        modulosDisponibles.usuarios,
        modulosDisponibles.admin_avanzado,
        modulosDisponibles.recepciones_equipos,
        modulosDisponibles.entregas_documentos,
        modulosDisponibles.movilidad,
      ];
    } else {
      // Otros admins respetan los permisos configurados
      if (loading) {
        items = [modulosDisponibles.dashboard];
      } else if (modulos.length === 0) {
        // Si no tiene permisos configurados, mostrar todos por defecto
        items = [
          modulosDisponibles.dashboard,
          modulosDisponibles.pendientes,
          modulosDisponibles.tickets,
          modulosDisponibles.control_horas,
          modulosDisponibles.calendario_visitas,
          modulosDisponibles.clientes,
          modulosDisponibles.equipos,
          modulosDisponibles.ordenes,
          modulosDisponibles.presupuestos,
          modulosDisponibles.agenda,
          modulosDisponibles.agendamientos_inteligentes,
          modulosDisponibles.usuarios,
          modulosDisponibles.admin_avanzado,
          modulosDisponibles.recepciones_equipos,
          modulosDisponibles.entregas_documentos,
          modulosDisponibles.movilidad,
        ];
      } else {
        // Mapear módulos del backend a las keys del frontend
        const modulosMapeados = modulos.map(modulo => {
          const mapeo = {
            'agendamientos': 'agenda',
            'panelAvanzado': 'admin_avanzado',
            'reportes': 'control_horas'
          };
          return mapeo[modulo] || modulo;
        });
        items = modulosMapeados
          .filter(key => modulosDisponibles[key])
          .map(key => modulosDisponibles[key])
          .filter(Boolean);
      }
    }
  } else if (rol === "cliente") {
    // Los clientes tienen módulos especiales
    if (loading) {
      // Mientras carga, mostrar dashboard por defecto
      items = itemsCliente.slice(0, 1).map(key => modulosDisponibles[key]);
    } else {
      items = itemsCliente
        .filter(key => puedeVer(key))
        .map(key => modulosDisponibles[key]);
    }
  } else if (rol === "tecnico") {
    // Para técnicos, filtrar según permisos
    if (loading) {
      // Mientras carga, mostrar módulos básicos
      items = [modulosDisponibles.dashboard];
    } else if (modulos.length === 0) {
      // Si no tiene permisos configurados, usar defaults de técnico

      items = [
        modulosDisponibles.dashboard,
        modulosDisponibles.tickets,
        modulosDisponibles.control_horas,
        modulosDisponibles.calendario_visitas,
        modulosDisponibles.ordenes,
        modulosDisponibles.presupuestos,
        modulosDisponibles.clientes,
        modulosDisponibles.equipos,
        modulosDisponibles.agenda,
        modulosDisponibles.recepciones_equipos,
        modulosDisponibles.entregas_documentos,
        modulosDisponibles.movilidad,
      ].filter(Boolean);
    } else {
      // Mapear módulos del backend a las keys del frontend

      const modulosMapeados = modulos.map(modulo => {
        // Algunos módulos tienen nombres diferentes en frontend
        const mapeo = {
          'agendamientos': 'agenda',
          'panelAvanzado': 'admin_avanzado',
          'reportes': 'control_horas'
        };
        return mapeo[modulo] || modulo;
      });
      
      items = modulosMapeados
        .filter(key => modulosDisponibles[key]) // Solo los que existen en el mapa
        .map(key => modulosDisponibles[key])
        .filter(Boolean);
    }
  } else {
    // Rol desconocido, mostrar solo dashboard
    items = [modulosDisponibles.dashboard];
  }

  // Guardar preferencias en localStorage
  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  useEffect(() => {
    localStorage.setItem('sidebar-pinned', JSON.stringify(isPinned));
  }, [isPinned]);

  // Lógica mejorada de auto-collapse - menos agresiva
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      // Solo auto-colapsar en pantallas realmente pequeñas y solo si está pinned
      if (width < 1024 && isPinned) {
        setIsCollapsed(true);
      } else if (width >= 1440 && isPinned) {
        // Auto-expandir en pantallas grandes
        setIsCollapsed(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isPinned]);

  // Determinar si mostrar expandido
  const shouldShowExpanded = isPinned ? !isCollapsed : isHovered;
  const sidebarWidth = shouldShowExpanded ? 'w-64' : 'w-16';
  const isDense = items.length >= 12;

  return (
    <>
      {/* Sidebar Desktop */}
      <aside 
        className={`hidden md:flex md:flex-col ${sidebarWidth} ${getThemeClasses('sidebar')} h-screen overflow-hidden transition-all duration-300 relative group z-30 shadow-2xl border-r border-white/10 flex-shrink-0 ${settings.glassEffect ? 'backdrop-blur-lg bg-opacity-90' : ''} ${
          settings.sidebarPosition === 'right' ? 'order-2' : ''
        }`}
        onMouseEnter={() => !isPinned && setIsHovered(true)}
        onMouseLeave={() => !isPinned && setIsHovered(false)}
      >
        <div className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-white/10 to-transparent" />
        {/* Header con gradiente */}
        <div className={`${settings.compactMode || isDense ? 'p-3' : 'p-4'} flex items-center justify-between border-b border-white/10 ${getThemeClasses('header')} ${shouldShowExpanded ? '' : 'px-2'} relative z-10`}>
          {shouldShowExpanded ? (
            <>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center shadow-md p-1 flex-shrink-0">
                  <img src={settings.customLogo || defaultLogo} alt="Logo" className="w-full h-full object-contain" />
                </div>
                <div className={`text-lg font-bold text-${currentTheme.sidebar.text.primary} drop-shadow-sm`}>
                  {settings.appName}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setIsPinned(!isPinned)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-all duration-200 hover:scale-105"
                  title={isPinned ? "Desanclar sidebar" : "Anclar sidebar"}
                >
                  {isPinned ? <Pin size={16} className={`text-${currentTheme.accent.primary}`} /> : <PinOff size={16} className="text-gray-400" />}
                </button>
                {isPinned && (
                  <button
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    className="p-2 hover:bg-white/10 rounded-lg transition-all duration-200 hover:scale-105"
                    title={isCollapsed ? "Expandir sidebar" : "Colapsar sidebar"}
                  >
                    <ChevronLeft className={`transition-all duration-300 ${isCollapsed ? 'rotate-180' : ''} text-gray-400 hover:text-white`} size={16} />
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="w-full flex justify-center">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-md p-1.5 transform hover:scale-105 transition-transform">
                <img src={settings.customLogo || defaultLogo} alt="Logo" className="w-full h-full object-contain" />
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className={`flex-1 overflow-y-auto overscroll-contain [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden ${settings.compactMode || isDense ? 'py-2' : 'py-4'} ${shouldShowExpanded ? 'px-3' : 'px-2'} ${settings.compactMode || isDense ? 'space-y-1' : 'space-y-1.5'} relative z-10`}>
          {!shouldShowExpanded && isPinned && (
            <div className="flex justify-center pb-3">
              <button
                onClick={() => setIsCollapsed(false)}
                className="p-2 hover:bg-white/10 rounded-lg transition-all duration-200 hover:scale-110 group"
                title="Expandir sidebar"
              >
                <Menu size={18} className={`text-gray-400 group-hover:text-${currentTheme.accent.hover} transition-colors`} />
              </button>
            </div>
          )}
          
          {items.map(({ key, label, icon: Icon, dividerBefore }) => (
            <React.Fragment key={key}>
              {dividerBefore && (
                <div className={shouldShowExpanded ? 'pt-2.5 pb-1 px-4' : 'pt-2 pb-1 px-1'}>
                  {shouldShowExpanded ? (
                    <span className={`text-[10px] font-bold uppercase tracking-[0.14em] opacity-55 text-${currentTheme.sidebar.text.muted}`}>
                      {dividerBefore}
                    </span>
                  ) : (
                    <div className="border-t border-current opacity-20" />
                  )}
                </div>
              )}
            <button
              onClick={() => onChange(key)}
              className={`relative flex items-center w-full rounded-xl text-sm transition-all duration-200 group/item focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70 ${settings.animationsEnabled ? 'transform hover:scale-[1.01]' : ''}
                ${shouldShowExpanded ? `gap-3 px-4 ${(settings.compactMode || isDense) ? 'py-2' : 'py-3'}` : `${(settings.compactMode || isDense) ? 'p-2' : 'p-3'} justify-center`}
                ${seccion === key
                  ? `${getThemeClasses('activeItem')} text-white shadow-lg ring-1 ring-white/20 ${getThemeClasses('glow')}` 
                  : `${getThemeClasses('hoverItem')} ${currentTheme.sidebar.text.secondary} hover:bg-white/10`
                }`}
              title={!shouldShowExpanded && settings.showTooltips ? label : ''}
            >
              <Icon 
                size={settings.compactMode ? 18 : 20} 
                className={`flex-shrink-0 transition-colors ${
                  seccion === key ? `text-${currentTheme.accent.hover}` : `group-hover/item:text-${currentTheme.accent.hover}`
                }`} 
              />
              {shouldShowExpanded && <span className="truncate font-semibold tracking-[0.01em]">{label}</span>}
              
              {/* Glow effect para item activo */}
              {seccion === key && settings.showBadges && (
                <div className={`absolute right-3 w-2 h-2 bg-${currentTheme.accent.hover} rounded-full animate-pulse shadow-lg shadow-${currentTheme.accent.glow}`}></div>
              )}
            </button>
            </React.Fragment>
          ))}
        </nav>

        {/* Footer fijo — user card + personalizar */}
        <div className={`flex-shrink-0 border-t border-white/10 ${getThemeClasses('header')} ${shouldShowExpanded ? 'p-3' : 'p-2'} relative z-10`}>
          {shouldShowExpanded ? (
            <>
              {/* User card */}
              {user && (
                <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-black/15 ring-1 ring-white/10 mb-2">
                  <div className={`w-7 h-7 rounded-full bg-gradient-to-br from-${currentTheme.accent.primary} to-${currentTheme.accent.secondary} flex-shrink-0 flex items-center justify-center text-white text-xs font-bold shadow`}>
                    {(user.nombre_completo || user.username || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-semibold text-${currentTheme.sidebar.text.primary} truncate leading-tight`}>
                      {user.nombre_completo || user.username}
                    </p>
                    <p className={`text-xs text-${currentTheme.sidebar.text.muted} capitalize leading-tight`}>{user.rol}</p>
                  </div>
                  <button
                    onClick={() => setShowCustomizer(true)}
                    className="p-1 hover:bg-white/10 rounded-md transition-colors flex-shrink-0"
                    title="Personalizar tema"
                  >
                    <Palette size={13} className={`text-${currentTheme.accent.hover} opacity-70`} />
                  </button>
                </div>
              )}
              {/* Tema activo */}
              <div className={`flex items-center gap-1.5 px-2 text-xs text-${currentTheme.sidebar.text.muted} opacity-60`}>
                <div className={`w-1.5 h-1.5 rounded-full animate-pulse bg-${currentTheme.accent.primary}`} />
                <span className="truncate">{currentTheme.name}</span>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center gap-2">
              {user && (
                <div
                  className={`w-7 h-7 rounded-full bg-gradient-to-br from-${currentTheme.accent.primary} to-${currentTheme.accent.secondary} flex items-center justify-center text-white text-xs font-bold cursor-default shadow`}
                  title={user.nombre_completo || user.username}
                >
                  {(user.nombre_completo || user.username || '?')[0].toUpperCase()}
                </div>
              )}
              <button
                onClick={() => setShowCustomizer(true)}
                className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                title="Personalizar tema"
              >
                <Palette size={13} className={`text-${currentTheme.accent.hover} opacity-70`} />
              </button>
            </div>
          )}
        </div>
      </aside>

      {!isPinned && isHovered && (
        <div 
          className="fixed inset-0 z-20 hidden md:block"
          onMouseEnter={() => setIsHovered(false)}
        />
      )}

      {/* Sidebar Móvil Mejorado */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 flex md:hidden">
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className={`relative w-72 max-w-[85vw] ${getThemeClasses('sidebar')} h-screen shadow-2xl z-50 border-r border-white/10 flex flex-col overflow-hidden`}>
            <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-white/10 to-transparent" />
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-3 right-3 text-white hover:bg-white/10 p-1.5 rounded-lg transition-all duration-200 hover:scale-105 z-10"
            >
              <X size={18} />
            </button>
            
            {/* Header móvil compacto para landscape */}
            <div className={`p-4 landscape:p-3 border-b border-white/10 ${getThemeClasses('header')} flex-shrink-0 relative z-10`}>
              <div className="flex items-center gap-2 landscape:gap-2 pr-10">
                <div className="w-8 h-8 landscape:w-7 landscape:h-7 bg-white rounded-lg landscape:rounded-md flex items-center justify-center shadow-md p-1 flex-shrink-0">
                  <img src={settings.customLogo || defaultLogo} alt="Logo" className="w-full h-full object-contain" />
                </div>
                <div className={`text-lg landscape:text-base font-bold text-${currentTheme.sidebar.text.primary} drop-shadow-sm truncate`}>
                  {settings.appName}
                </div>
              </div>
            </div>
            
            {/* Navegación móvil con scroll optimizada para landscape */}
            <nav className="flex-1 overflow-y-auto overscroll-contain p-3 landscape:p-2 space-y-1.5 landscape:space-y-1 pb-4 relative z-10">
              {items.map(({ key, label, icon: Icon, dividerBefore }) => (
                <React.Fragment key={key}>
                  {dividerBefore && (
                    <div className="pt-2 pb-0.5 px-3">
                      <span className={`text-[10px] font-bold uppercase tracking-[0.14em] opacity-55 text-${currentTheme.sidebar.text.muted}`}>
                        {dividerBefore}
                      </span>
                    </div>
                  )}
                <button
                  onClick={() => {
                    onChange(key);
                    setMobileOpen(false);
                  }}
                  className={`relative flex items-center gap-3 landscape:gap-2 w-full px-3 landscape:px-2 py-2.5 landscape:py-2 rounded-lg landscape:rounded-md text-sm transition-all duration-200 ${settings.animationsEnabled ? 'transform hover:scale-[1.02]' : ''}
                    ${seccion === key
                      ? `${getThemeClasses('activeItem')} text-white shadow-lg ring-1 ring-white/20`
                      : `${getThemeClasses('hoverItem')} ${currentTheme.sidebar.text.secondary} hover:bg-white/10`
                    }`}
                >
                  <Icon 
                    size={18} 
                    className={`flex-shrink-0 landscape:w-4 landscape:h-4 ${
                      seccion === key ? `text-${currentTheme.accent.hover}` : 'text-gray-400'
                    }`} 
                  />
                  <span className="font-medium text-sm landscape:text-xs">{label}</span>
                  
                  {/* Glow effect */}
                  {seccion === key && settings.showBadges && (
                    <div className={`absolute right-3 w-1.5 h-1.5 landscape:w-1 landscape:h-1 bg-${currentTheme.accent.hover} rounded-full animate-pulse shadow-lg shadow-${currentTheme.accent.glow}`}></div>
                  )}
                </button>
                </React.Fragment>
              ))}
            </nav>

            {/* Footer móvil compacto para landscape */}
            <div className={`p-3 landscape:p-2 border-t border-white/10 ${getThemeClasses('header')} flex-shrink-0 relative z-10`}>
              {user && (
                <div className="flex items-center gap-2 px-1 py-1.5 mb-2 rounded-lg bg-black/10">
                  <div className={`w-7 h-7 rounded-full bg-gradient-to-br from-${currentTheme.accent.primary} to-${currentTheme.accent.secondary} flex-shrink-0 flex items-center justify-center text-white text-xs font-bold`}>
                    {(user.nombre_completo || user.username || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs font-semibold text-${currentTheme.sidebar.text.primary} truncate`}>
                      {user.nombre_completo || user.username}
                    </div>
                    <div className={`text-xs text-${currentTheme.sidebar.text.muted} capitalize`}>{user.rol}</div>
                  </div>
                </div>
              )}
              <button
                onClick={() => {
                  setShowCustomizer(true);
                  setMobileOpen(false);
                }}
                className="flex items-center gap-2 landscape:gap-1.5 w-full px-3 landscape:px-2 py-2.5 landscape:py-1.5 hover:bg-white/10 rounded-lg landscape:rounded-md transition-all duration-200 hover:scale-[1.02]"
                title="Personalizar tema"
              >
                <Palette size={18} className={`landscape:w-4 landscape:h-4 text-${currentTheme.accent.hover}`} />
                <span className={`text-${currentTheme.sidebar.text.secondary} font-medium text-sm landscape:text-xs`}>
                  Personalizar Tema
                </span>
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Componente de personalización */}
      <ThemeCustomizer 
        isOpen={showCustomizer}
        onClose={() => setShowCustomizer(false)}
      />
    </>
  );
}